// ChartComponents.jsx (React.js + JavaScript + MUI + CSS Modules version)

import React, { createContext, useContext, useId, useMemo, forwardRef } from 'react';
import * as Recharts from 'recharts';
import clsx from 'clsx';
import './ChartComponents.css';

const THEMES = { light: '', dark: '.dark' };
const ChartContext = createContext(null);

export function useChart() {
  const context = useContext(ChartContext);
  if (!context) throw new Error('useChart must be used within a <ChartContainer />');
  return context;
}

export const ChartContainer = forwardRef(({ id, className, children, config, ...props }, ref) => {
  const uniqueId = useId();
  const chartId = `chart-${id || uniqueId.replace(/:/g, '')}`;

  return (
    <ChartContext.Provider value={{ config }}>
      <div
        data-chart={chartId}
        ref={ref}
        className={clsx("chartContainer", className)}
        {...props}
        style={{
          width: '100%',
          height: '100%',
        }}
      >
        <ChartStyle id={chartId} config={config} />
        <Recharts.ResponsiveContainer width="100%" height="100%">
          {children}
        </Recharts.ResponsiveContainer>
      </div>
    </ChartContext.Provider>
  );
});

export function ChartStyle({ id, config }) {
  const colorConfig = Object.entries(config).filter(([_, cfg]) => cfg.theme || cfg.color);

  if (!colorConfig.length) return null;

  const styleContent = Object.entries(THEMES)
    .map(([theme, prefix]) => {
      const themeStyles = colorConfig
        .map(([key, cfg]) => {
          const color = cfg.theme?.[theme] || cfg.color;
          return color ? `  --color-${key}: ${color};` : null;
        })
        .filter(Boolean)
        .join('\n');
      return `${prefix} [data-chart=${id}] {\n${themeStyles}\n}`;
    })
    .join('\n');

  return <style dangerouslySetInnerHTML={{ __html: styleContent }} />;
}

export const ChartTooltip = Recharts.Tooltip;

export const ChartTooltipContent = forwardRef(({
  active,
  payload,
  className,
  indicator = 'dot',
  hideLabel = false,
  label,
  labelClassName,
  labelKey,
  defaultColor,
  dataKeys,
  dataLabels,
  CustomTooltip,
  showPercentage = false,
  tooltipFormatter = (v) => v
}, ref) => {
  const tooltipLabel = useMemo(() => {
    if (hideLabel || !payload?.length) return null;
    const item = payload[0];

    if (CustomTooltip) return <CustomTooltip {...item} />;

    return (
      <div className={"chartTooltipLabel"}>
        <div
          className={"tooltipColor"}
          style={{ backgroundColor: item.payload?.isCurrent ? '#00B2FF' : defaultColor , width: '10px', height: '10px' , borderRadius: '2px' }}
        />
        <div>
          {dataKeys?.map((k, i) => (
            <div key={k}>
              {item.payload[dataLabels?.[i] || k]} : {tooltipFormatter(item.payload[k])}{showPercentage ? '%' : ''}
            </div>
          ))}
        </div>
      </div>
    );
  }, [hideLabel, payload, CustomTooltip, defaultColor, dataKeys, dataLabels, tooltipFormatter, showPercentage]);

  if (!active || !payload?.length) return null;

  return (
    <div ref={ref} className={clsx("chartTooltip")}>
      {tooltipLabel}
    </div>
  );
});

export const ChartLegend = Recharts.Legend;

export const ChartLegendContent = forwardRef(({ className, hideIcon = false, payload, verticalAlign = 'bottom', nameKey }, ref) => {
  const { config } = useChart();
  if (!payload?.length) return null;

  return (
    <div ref={ref} className={clsx("chartLegend", className)}>
      {payload.map((item) => {
        const key = nameKey || item.dataKey || 'value';
        const itemConfig = getPayloadConfigFromPayload(config, item, key);

        return (
          <div key={item.value} className={"legendItem"}>
            {!hideIcon && (
              itemConfig?.icon ? <itemConfig.icon /> : <div className={"legendColor"} style={{ backgroundColor: item.color }} />
            )}
            {itemConfig?.label || item.value}
          </div>
        );
      })}
    </div>
  );
});

function getPayloadConfigFromPayload(config, payload, key) {
  const p = payload?.payload || {};
  const labelKey = typeof payload?.[key] === 'string' ? payload[key] : p?.[key];
  return config[labelKey] || config[key];
}
